import React, { useState, useEffect, useMemo, useRef } from 'react';
import API_GATEWAY_URL from '../../config';
import './DashboardTab.css';
import axios from 'axios';
import Map, { Source, Layer } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SyncIcon from '@mui/icons-material/Sync';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { Tooltip } from '@mui/material';
import moment from 'moment';

import palm_count from '../../assets/palm_count.png';
import palm_canopy_size from '../../assets/palm_canopy_size.png';
import palm_distance from '../../assets/palm_distance.png';
import palm_classification from '../../assets/palm_classification.png';

import palm_total_icon from '../../assets/palm_total.png';
import area_icon from '../../assets/area.png';
import sph_icon from '../../assets/sph.png';
import palm_yellow_icon from '../../assets/palm_yellow.png';
import palm_normal_icon from '../../assets/palm_normal.png';
import threshold_icon from '../../assets/distance_threshold.png';

const toolIcons = {
  'Palm Location': palm_count,
  'Palm Canopy Size': palm_canopy_size,
  'Palm Distance': palm_distance,
  'Palm Yellowish Classification': palm_classification
};

function renderToolIcons(inputString) {
  if (!inputString) return null;
  let toolStr = inputString;
  if (Array.isArray(inputString)) {
    toolStr = inputString.join(",");
  }
  const tools = toolStr.split(",").map((word) => {
    return word.trim()
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  });

  return (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
      {tools.map((tool, index) => (
        <Tooltip title={tool} key={index} arrow>
          <img
            src={toolIcons[tool]}
            alt={tool}
            style={{ width: '28px', height: '28px', objectFit: 'contain', borderRadius: '4px' }}
          />
        </Tooltip>
      ))}
    </div>
  );
}

const mapStyle = {
  version: 8,
  sources: {
    'esri-satellite': {
      type: 'raster',
      tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'],
      tileSize: 256
    }
  },
  layers: [
    {
      id: 'esri-satellite-layer',
      type: 'raster',
      source: 'esri-satellite',
      minzoom: 0,
      maxzoom: 22
    }
  ]
};

const calculateBBox = (geojson) => {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const processCoord = (coord) => {
    if (coord[0] < minX) minX = coord[0];
    if (coord[1] < minY) minY = coord[1];
    if (coord[0] > maxX) maxX = coord[0];
    if (coord[1] > maxY) maxY = coord[1];
  };
  const processFeature = (geometry) => {
    if (geometry.type === 'Polygon') {
      geometry.coordinates.forEach(ring => ring.forEach(processCoord));
    } else if (geometry.type === 'MultiPolygon') {
      geometry.coordinates.forEach(poly => poly.forEach(ring => ring.forEach(processCoord)));
    } else if (geometry.type === 'Point') {
      processCoord(geometry.coordinates);
    }
  };
  if (geojson.features) {
    geojson.features.forEach(f => processFeature(f.geometry));
  } else if (geojson.geometry) {
    processFeature(geojson.geometry);
  }
  return [[minX, minY], [maxX, maxY]];
};

const DashboardTab = () => {
  const mapRef = useRef(null);
  const [jobs, setJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Layer Toggles
  const [showRaster, setShowRaster] = useState(true);
  const [showLocation, setShowLocation] = useState(true);
  const [showCanopy, setShowCanopy] = useState(true);
  const [showDistance, setShowDistance] = useState(true);

  // Analytical Data
  const [palmLocationData, setPalmLocationData] = useState(null);
  const [closelyPlantedList, setCloselyPlantedList] = useState([]);
  const [thinningFilter, setThinningFilter] = useState(null);
  const [classFilter, setClassFilter] = useState(null);
  const [padFilter, setPadFilter] = useState(null);
  const [selectedPalmId, setSelectedPalmId] = useState(null);

  useEffect(() => {
    let isFetching = false;

    const fetchJobs = async () => {
      if (isFetching) return;
      isFetching = true;

      const jwtToken = localStorage.getItem('palm_care_token');
      if (!jwtToken) {
        setError('User is not authenticated. JWT token missing.');
        setIsLoading(false);
        isFetching = false;
        return;
      }

      try {
        const response = await axios.get(`${API_GATEWAY_URL}/jobs`, {
          headers: { Authorization: jwtToken },
          params: { page: 1 }
        });

        const fetchedJobs = response.data.jobs || [];
        setJobs(fetchedJobs);

        // Only default to first completed if nothing is currently selected
        setSelectedJobId(prevId => {
          if (!prevId) {
            const firstCompleted = fetchedJobs.find(job => job?.state?.toLowerCase() === 'completed' && job?.download_url);
            return firstCompleted ? firstCompleted.id : prevId;
          }
          return prevId;
        });
      } catch (err) {
        console.error("Error fetching jobs:", err);
        setError("Failed to load jobs: " + (err.message || ""));
      } finally {
        setIsLoading(false);
        isFetching = false;
      }
    };

    fetchJobs();

    const intervalId = setInterval(fetchJobs, 10000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const storedJobId = localStorage.getItem('dashboard_selected_job_id');
    if (storedJobId && jobs.length > 0) {
      const job = jobs.find(j => j.id === storedJobId);
      if (job) {
        setSelectedJobId(storedJobId);
        localStorage.removeItem('dashboard_selected_job_id');
      }
    }
  }, [jobs]);

  const selectedJob = useMemo(() => {
    return jobs.find(job => job.id === selectedJobId) || null;
  }, [jobs, selectedJobId]);

  const dashboardUrls = useMemo(() => {
    if (!selectedJob) return null;
    if (selectedJob.state.toLowerCase() !== 'completed') return null;
    try {
      const parsed = JSON.parse(selectedJob.download_url);
      return parsed.dashboard || null;
    } catch (e) {
      return null;
    }
  }, [selectedJob]);

  // Extract Close Planting Threshold from parameters
  const closePlantingThreshold = useMemo(() => {
    if (!selectedJob) return 8.5; // fallback
    try {
      const params = JSON.parse(selectedJob.parameters);
      return params.distance_threshold || 8.5;
    } catch (e) {
      return 8.5;
    }
  }, [selectedJob]);

  const dashboardUrlsStr = dashboardUrls ? JSON.stringify(dashboardUrls) : null;
  const selectedJobIdStr = selectedJob ? selectedJob.id : null;
  const selectedUserIdStr = selectedJob ? selectedJob.user_id : null;

  // Fetch GeoJSONs for Analytics when job changes
  useEffect(() => {
    let active = true;

    const currentDashboardUrls = dashboardUrlsStr ? JSON.parse(dashboardUrlsStr) : null;

    if (!currentDashboardUrls || !selectedJobIdStr) {
      setPalmLocationData(null);
      setCloselyPlantedList([]);
      setThinningFilter(null);
      setSelectedPalmId(null);
      return;
    }

    const loadGeoJSON = async () => {
      let locationRes = null;
      if (currentDashboardUrls.palm_location) {
        try {
          locationRes = await axios.get(currentDashboardUrls.palm_location);
          if (locationRes.data && locationRes.data.features) {
            locationRes.data.features = locationRes.data.features.filter(
              f => f.properties && String(f.properties.Job_ID) === String(selectedJobIdStr)
            );
          }
          if (active) setPalmLocationData(locationRes.data);
        } catch (err) {
          console.warn("Failed to fetch palm_location:", err);
        }
      }

      if (currentDashboardUrls.job_boundary) {
        try {
          const boundaryRes = await axios.get(currentDashboardUrls.job_boundary);
          const bbox = calculateBBox(boundaryRes.data);
          if (mapRef.current && active) {
            mapRef.current.fitBounds(bbox, { padding: 40, duration: 1000 });
          }
        } catch (err) {
          console.warn("Failed to fetch job_boundary:", err);
        }
      }

      // Calculate Closely Planted list directly from palm_location GeoJSON properties
      if (locationRes && locationRes.data && locationRes.data.features) {
        const features = locationRes.data.features || [];
        let closePalms = features
          .map(f => ({
            id: f.properties.Index,
            x: f.geometry.coordinates[0],
            y: f.geometry.coordinates[1],
            NearPalm: f.properties.NearPalm,
            PAD: f.properties.PAD,
            Class: f.properties.Class
          }));

        closePalms.sort((a, b) => a.id - b.id);
        if (active) {
          setCloselyPlantedList(closePalms);
          setThinningFilter(null);
          setClassFilter(null);
          setPadFilter(null);
        }
      } else {
        if (active) {
          setCloselyPlantedList([]);
          setThinningFilter(null);
          setClassFilter(null);
          setPadFilter(null);
        }
      }
    };

    loadGeoJSON();

    return () => { active = false; };
  }, [dashboardUrlsStr, selectedJobIdStr, selectedUserIdStr]);

  // Derived Metrics
  const totalPalms = palmLocationData ? palmLocationData.features.length : 0;
  const areaHa = selectedJob ? selectedJob.size : 0;
  const sph = areaHa > 0 ? (totalPalms / areaHa).toFixed(0) : 0;

  const isToolSelected = (tool) => {
    if (!selectedJob || !selectedJob.selected_tools) return false;
    if (Array.isArray(selectedJob.selected_tools)) return selectedJob.selected_tools.includes(tool);
    return selectedJob.selected_tools.split(",").map(t => t.trim()).includes(tool);
  };

  const toolCanopySize = isToolSelected('palm_canopy_size');
  const toolPalmDistance = isToolSelected('palm_distance');
  const toolYellowish = isToolSelected('palm_yellowish_classification');

  const hasYellowishClassification = useMemo(() => {
    if (!palmLocationData || !palmLocationData.features) return false;
    return palmLocationData.features.some(f => f.properties.Class != null && f.properties.Class !== '');
  }, [palmLocationData]);

  const yellowishPalms = palmLocationData ? palmLocationData.features.filter(f => f.properties.Class === 'Yellowish').length : 0;
  const healthyPalms = palmLocationData ? palmLocationData.features.filter(f => f.properties.Class === 'Normal').length : 0;
  const healthyPercentage = totalPalms > 0 ? ((healthyPalms / totalPalms) * 100).toFixed(1) : 0;
  const yellowishPercentage = totalPalms > 0 ? ((yellowishPalms / totalPalms) * 100).toFixed(1) : 0;

  const palms1Close = closelyPlantedList.filter(p => p.NearPalm === 1).length;
  const palms2Close = closelyPlantedList.filter(p => p.NearPalm === 2).length;
  const palmsMoreClose = closelyPlantedList.filter(p => p.NearPalm > 2).length;

  const filteredCloselyPlantedList = useMemo(() => {
    let list = closelyPlantedList;
    if (thinningFilter === '1') list = list.filter(p => p.NearPalm === 1);
    else if (thinningFilter === '2') list = list.filter(p => p.NearPalm === 2);
    else if (thinningFilter === '>2') list = list.filter(p => p.NearPalm > 2);

    if (classFilter === 'Normal') list = list.filter(p => p.Class === 'Normal');
    else if (classFilter === 'Yellowish') list = list.filter(p => p.Class === 'Yellowish');

    if (padFilter) list = list.filter(p => p.PAD === padFilter);

    if (thinningFilter) {
      list = [...list].sort((a, b) => b.NearPalm - a.NearPalm || a.id - b.id);
    }

    return list;
  }, [closelyPlantedList, thinningFilter, classFilter, padFilter]);

  const padCounts = useMemo(() => {
    if (!palmLocationData || !palmLocationData.features) return [];
    const counts = {};
    palmLocationData.features.forEach(f => {
      const pad = f.properties.PAD;
      if (pad) {
        counts[pad] = (counts[pad] || 0) + 1;
      }
    });

    const sortOrder = [
      "<12 Months",
      "13-24 Months",
      "25-36 Months",
      "37-48 Months",
      "5 Years",
      "6 Years",
      "7 Years",
      ">7 Years"
    ];

    return Object.entries(counts).sort((a, b) => {
      const idxA = sortOrder.indexOf(a[0]);
      const idxB = sortOrder.indexOf(b[0]);
      if (idxA === -1 && idxB === -1) return a[0].localeCompare(b[0]);
      if (idxA === -1) return 1;
      if (idxB === -1) return -1;
      return idxA - idxB;
    });
  }, [palmLocationData]);

  const handleJobClick = (job) => {
    if (job.state.toLowerCase() === 'completed') {
      setSelectedJobId(job.id);
    }
  };

  const handleZoomToPalm = (palm) => {
    if (selectedPalmId === palm.id) {
      setSelectedPalmId(null);
      return;
    }
    setSelectedPalmId(palm.id);
    if (mapRef.current) {
      mapRef.current.flyTo({
        center: [palm.x, palm.y],
        zoom: 20,
        duration: 1000
      });
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="calc(100vh - 90px)">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className="dashboard__tab__container">

      {/* Top Bar: Jobs List */}
      <div className="dashboard__top__bar" style={{ overflow: 'hidden', paddingRight: '16px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: '150px', paddingRight: '16px', borderRight: '2px solid #e0e0e0', marginRight: '0', flexShrink: 0 }}>
          <Typography variant="h6" sx={{ color: '#002D62', fontWeight: 'bold', textAlign: 'center', lineHeight: 1.2, mb: 1 }}>
            LIST OF JOBS
          </Typography>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexDirection: 'column' }}>
            <Button variant="contained" size="small" sx={{ backgroundColor: '#002D62', fontWeight: 'bold', fontSize: '12px' }} onClick={() => window.dispatchEvent(new CustomEvent('changeTab', { detail: 'Tools' }))}>
              SUBMIT JOB
            </Button>
            <Button variant="outlined" size="small" sx={{ color: '#002D62', borderColor: '#002D62', fontWeight: 'bold', fontSize: '12px' }} onClick={() => window.dispatchEvent(new CustomEvent('changeTab', { detail: 'Data' }))}>
              UPLOAD DATA
            </Button>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'row', gap: '16px', overflowX: 'auto', flex: 1, paddingBottom: '4px', alignItems: 'center' }}>
          {error ? (
            <Typography color="error" variant="body2">{error}</Typography>
          ) : (
            jobs.map(job => {
              const isError = job.state.toLowerCase() === 'error';
              const isRunning = job.state.toLowerCase() === 'running';
              const isSelected = job.id === selectedJobId;

              let className = "job__card";
              if (isSelected) className += " selected";
              if (isError) className += " error";

              return (
                <div key={job.id} className={className} onClick={() => handleJobClick(job)}>
                  {/* Line 1: Job ID & Status */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#002D62' }}>
                      {job.estate || job.name || job.id.substring(0, 8)}
                    </Typography>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {isError ? (
                        <ErrorOutlineIcon sx={{ color: '#ff0000ff', fontSize: '16px' }} />
                      ) : isRunning ? (
                        <SyncIcon sx={{ color: '#0059ffff', fontSize: '16px' }} />
                      ) : job.state.toLowerCase() === 'pending' ? (
                        <HourglassEmptyIcon sx={{ color: '#0059ffff', fontSize: '16px' }} />
                      ) : (
                        <CheckCircleOutlineIcon sx={{ color: '#1b5e20', fontSize: '16px' }} />
                      )}
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 900,
                          lineHeight: 1,
                          paddingTop: '2px', // Slight adjustment for baseline centering with the icon
                          color: isError ? '#ff0000ff' : ((isRunning || job.state.toLowerCase() === 'pending') ? '#0059ffff' : '#1b5e20')
                        }}
                      >
                        {job.state.toUpperCase()}
                      </Typography>
                    </div>
                  </div>

                  {/* Line 2: Block & Ha */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <Typography variant="body2" sx={{ color: '#333', fontWeight: 'bold' }}>
                      Block: {job.block}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#555', fontWeight: 'bold', fontSize: '14px' }}>
                      {job.size} Ha
                    </Typography>
                  </div>

                  {/* Line 3: Selected Tools & Date/Time */}
                  <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                      <Typography variant="body2" sx={{ color: '#777', fontSize: '11px', fontWeight: 'bold', marginBottom: '4px', textAlign: 'left' }}>
                        Selected Tools:
                      </Typography>
                      <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                        {renderToolIcons(job.selected_tools)}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <Typography variant="caption" display="block" sx={{ color: '#666', fontStyle: 'italic' }}>
                        {moment.utc(job.created_at).utcOffset(8).format('DD/MM/YYYY')}
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ color: '#666', fontStyle: 'italic' }}>
                        {moment.utc(job.created_at).utcOffset(8).format('h:mm:ss a')}
                      </Typography>
                    </div>
                  </div>
                </div>
              );
            }))}
        </div>


      </div>

      <div className="dashboard__main__content">
        {/* Left Sidebar: Analytics */}
        <div className="dashboard__left__sidebar">
          {/* Palm Smart Thinning Container */}
          <div className="analytics__container" style={{ border: '2px solid #002D62', borderRadius: '8px', padding: '16px', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" sx={{ color: '#002D62', mb: 1, fontWeight: 'bold', textTransform: 'uppercase' }}>
              Palm Statistics
            </Typography>

            {selectedJob && dashboardUrls ? (
              <div className="metric__row" style={{ display: 'flex', flexDirection: 'row', gap: '8px', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '8px' }}>
                    <img src={palm_total_icon} alt="Palm Counted" style={{ width: 38, height: 38, objectFit: 'contain' }} />
                    <Typography className="metric__value" style={{ fontSize: '24px', fontWeight: 'bold', lineHeight: 1, margin: 0, padding: 0, whiteSpace: 'nowrap' }}>
                      {totalPalms.toLocaleString()}
                    </Typography>
                  </div>
                  <Typography className="metric__title" style={{ fontSize: '11px', textAlign: 'center', width: '100%', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Palms Counted</Typography>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '8px' }}>
                    <img src={area_icon} alt="Area" style={{ width: 38, height: 38, objectFit: 'contain' }} />
                    <Typography className="metric__value" style={{ fontSize: '24px', fontWeight: 'bold', lineHeight: 1, margin: 0, padding: 0, whiteSpace: 'nowrap' }}>
                      {areaHa}
                    </Typography>
                  </div>
                  <Typography className="metric__title" style={{ fontSize: '11px', textAlign: 'center', width: '100%', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Area Size (Ha)</Typography>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '8px' }}>
                    <img src={sph_icon} alt="SPH" style={{ width: 38, height: 38, objectFit: 'contain' }} />
                    <Typography className="metric__value" style={{ fontSize: '24px', fontWeight: 'bold', lineHeight: 1, margin: 0, padding: 0, whiteSpace: 'nowrap' }}>
                      {sph}
                    </Typography>
                  </div>
                  <Typography className="metric__title" style={{ fontSize: '11px', textAlign: 'center', width: '100%', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>SPH</Typography>
                </div>
              </div>
            ) : (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" flex={1}>
                <Typography variant="body2" color="textSecondary" align="center">Select a completed job to view analytics.</Typography>
              </Box>
            )}
          </div>

          {/* Palm Classification Container */}
          <div className="analytics__container" style={{ border: '2px solid #002D62', borderRadius: '8px', padding: '16px', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" sx={{ color: '#002D62', mb: 1, fontWeight: 'bold', textTransform: 'uppercase' }}>
              Palm Classification
            </Typography>
            {selectedJob && dashboardUrls ? (
              toolYellowish ? (
                <div style={{ display: 'flex', flexDirection: 'row', gap: '8px', justifyContent: 'center', alignItems: 'flex-start' }}>
                  <div
                    style={{ flex: 1, cursor: 'pointer', padding: '4px', backgroundColor: classFilter === 'Normal' ? '#e8f0fe' : 'transparent', borderRadius: '4px' }}
                    onClick={() => setClassFilter(classFilter === 'Normal' ? null : 'Normal')}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', marginBottom: '4px' }}>
                      <img src={palm_normal_icon} alt="Normal Palms" style={{ width: 38, height: 38, objectFit: 'contain' }} />
                      <Typography className="metric__sub_value" style={{ fontSize: '24px', fontWeight: 'bold', lineHeight: 1, color: '#1b5e20', margin: 0, padding: 0, whiteSpace: 'nowrap' }}>
                        {healthyPalms.toLocaleString()} <span style={{ fontSize: '14px' }}>({healthyPercentage}%)</span>
                      </Typography>
                    </div>
                    <Typography className="metric__title" style={{ fontSize: '11px', textAlign: 'center', width: '100%', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Normal Palms</Typography>
                  </div>
                  <div
                    style={{ flex: 1, cursor: 'pointer', padding: '4px', backgroundColor: classFilter === 'Yellowish' ? '#e8f0fe' : 'transparent', borderRadius: '4px' }}
                    onClick={() => setClassFilter(classFilter === 'Yellowish' ? null : 'Yellowish')}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', marginBottom: '4px' }}>
                      <img src={palm_yellow_icon} alt="Yellowish Palms" style={{ width: 38, height: 38, objectFit: 'contain' }} />
                      <Typography className="metric__value" style={{ fontSize: '24px', fontWeight: 'bold', lineHeight: 1, color: '#b8860b', margin: 0, padding: 0, whiteSpace: 'nowrap' }}>
                        {yellowishPalms.toLocaleString()} <span style={{ fontSize: '14px' }}>({yellowishPercentage}%)</span>
                      </Typography>
                    </div>
                    <Typography className="metric__title" style={{ fontSize: '11px', textAlign: 'center', width: '100%', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Yellowish Palms</Typography>
                  </div>
                </div>
              ) : (
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" flex={1}>
                  <Typography variant="body2" color="textSecondary" align="center">Analysis is not selected</Typography>
                </Box>
              )
            ) : (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" flex={1}>
                <Typography variant="body2" color="textSecondary" align="center">Select a completed job to view analytics.</Typography>
              </Box>
            )}
          </div>

          {/* Palm Age Determination Container */}
          <div className="analytics__container" style={{ border: '2px solid #002D62', borderRadius: '8px', padding: '16px', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Typography variant="h6" sx={{ color: '#002D62', mb: 0.5, fontWeight: 'bold', textTransform: 'uppercase' }}>
              Palm Age Determination
            </Typography>
            {/* <Typography variant="caption" sx={{ color: '#64748b', mb: 2, display: 'block' }}>
              Actual age of palm based on canopy size
            </Typography> */}
            {selectedJob && dashboardUrls ? (
              toolCanopySize ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', flex: 1, overflowY: 'auto', paddingRight: '8px' }}>
                  {padCounts.map(([padClass, count], idx) => (
                    <div
                      key={idx}
                      onClick={() => setPadFilter(padFilter === padClass ? null : padClass)}
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        borderBottom: '1px solid #e0e0e0',
                        padding: '4px',
                        cursor: 'pointer',
                        backgroundColor: padFilter === padClass ? '#e8f0fe' : 'transparent',
                        borderRadius: '4px'
                      }}
                    >
                      <Typography className="metric__value" style={{ fontSize: '24px', fontWeight: 'bold', lineHeight: 1, margin: 0, padding: 0, whiteSpace: 'nowrap', marginRight: '8px' }}>
                        {count.toLocaleString()}
                      </Typography>
                      <Typography className="metric__title" style={{ fontSize: '11px', margin: 0, color: '#888' }}>
                        Palm(s) with actual age of {padClass}
                      </Typography>
                    </div>
                  ))}
                </div>
              ) : (
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" flex={1}>
                  <Typography variant="body2" color="textSecondary" align="center">Analysis is not selected</Typography>
                </Box>
              )
            ) : (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" flex={1}>
                <Typography variant="body2" color="textSecondary" align="center">Select a completed job to view analytics.</Typography>
              </Box>
            )}
          </div>
        </div>

        {/* Center Map */}
        <div className="dashboard__center__map">
          {/* Layer Toggles */}
          <div className="layer__toggles" style={{ position: 'absolute', top: 16, right: 16, background: 'white', padding: '12px', borderRadius: '8px', zIndex: 5, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Layers</Typography>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '14px', display: 'flex', alignItems: 'center' }}>
                <input type="checkbox" checked={showRaster} onChange={(e) => setShowRaster(e.target.checked)} style={{ marginRight: '8px' }} />
                Raster
              </label>
              <label style={{ fontSize: '14px', display: 'flex', alignItems: 'center' }}>
                <input type="checkbox" checked={showCanopy} onChange={(e) => setShowCanopy(e.target.checked)} style={{ marginRight: '8px' }} />
                Canopy
              </label>
              <label style={{ fontSize: '14px', display: 'flex', alignItems: 'center' }}>
                <input type="checkbox" checked={showDistance} onChange={(e) => setShowDistance(e.target.checked)} style={{ marginRight: '8px' }} />
                Distance
              </label>
              <label style={{ fontSize: '14px', display: 'flex', alignItems: 'center' }}>
                <input type="checkbox" checked={showLocation} onChange={(e) => setShowLocation(e.target.checked)} style={{ marginRight: '8px' }} />
                Location
              </label>
            </div>
          </div>

          {/* Legend */}
          <div className="map__legend" style={{ position: 'absolute', bottom: 32, right: 16, background: 'white', padding: '12px', borderRadius: '8px', zIndex: 5, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Legend</Typography>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {hasYellowishClassification ? (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#00ff00', border: '1px solid #000' }}></div>
                    <Typography variant="caption">Normal Palm</Typography>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#ffff00', border: '1px solid #000' }}></div>
                    <Typography variant="caption">Yellowish Palm</Typography>
                  </div>
                </>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#808080', border: '1px solid #000' }}></div>
                  <Typography variant="caption">Palm Count</Typography>
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '12px', height: '12px', border: '2px solid #0000ff' }}></div>
                <Typography variant="caption">Palm Canopy</Typography>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '16px', height: '3px', backgroundColor: '#ff0000' }}></div>
                <Typography variant="caption">Closely Planted</Typography>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '12px', height: '12px', borderRadius: '50%', border: '2px solid #ff00ff' }}></div>
                <Typography variant="caption">Selected Palm</Typography>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '16px', height: '3px', borderTop: '2px dashed #ffcc00' }}></div>
                <Typography variant="caption">Block Boundary</Typography>
              </div>
            </div>
          </div>

          <Map
            ref={mapRef}
            initialViewState={{ longitude: 109.0, latitude: 4.0, zoom: 5 }}
            style={{ width: '100%', height: '100%' }}
            mapStyle={mapStyle}
            maxZoom={22}
          >
            {showRaster && dashboardUrls && dashboardUrls.tiling && (
              <Source id="raster-source" type="raster" tiles={[dashboardUrls.tiling.includes('{z}') ? dashboardUrls.tiling : `${dashboardUrls.tiling.replace(/\/$/, '')}/{z}/{x}/{y}.png`]} tileSize={256}>
                <Layer id="raster-layer" type="raster" minzoom={14} maxzoom={22} />
              </Source>
            )}

            {dashboardUrls && dashboardUrls.job_boundary && (
              <Source id="job-boundary-source" type="geojson" data={dashboardUrls.job_boundary}>
                <Layer id="job-boundary-layer" type="line" filter={['==', ['get', 'Job_ID'], selectedJob.id]} paint={{ 'line-color': '#ffcc00', 'line-width': 3, 'line-dasharray': [2, 2] }} />
              </Source>
            )}

            {showCanopy && dashboardUrls && dashboardUrls.palm_canopy && (
              <Source id="palm-canopy-source" type="geojson" data={dashboardUrls.palm_canopy}>
                <Layer id="palm-canopy-layer" type="fill" filter={['any', ['!', ['has', 'Job_ID']], ['==', ['get', 'Job_ID'], selectedJob.id]]} paint={{ 'fill-opacity': 0, 'fill-outline-color': '#0000ff' }} />
                <Layer id="palm-canopy-line-layer" type="line" filter={['any', ['!', ['has', 'Job_ID']], ['==', ['get', 'Job_ID'], selectedJob.id]]} paint={{ 'line-color': '#0000ff', 'line-width': 2 }} />
              </Source>
            )}

            {showDistance && dashboardUrls && dashboardUrls.palm_distance && (
              <Source id="palm-distance-source" type="geojson" data={dashboardUrls.palm_distance}>
                <Layer id="palm-distance-layer" type="line" filter={['==', ['get', 'Job_ID'], selectedJob.id]} paint={{
                  'line-color': ['case', ['==', ['get', 'cp'], 'closely planted'], '#ff0000', '#ffffff'],
                  'line-width': ['case', ['==', ['get', 'cp'], 'closely planted'], 3, 1]
                }}
                />
              </Source>
            )}

            {showLocation && dashboardUrls && dashboardUrls.palm_location && (
              <Source id="palm-location-source" type="geojson" data={dashboardUrls.palm_location}>
                <Layer id="palm-location-layer" type="circle" filter={['==', ['get', 'Job_ID'], selectedJob.id]} paint={{
                  'circle-radius': 4,
                  'circle-color': [
                    'case',
                    ['==', ['get', 'Class'], 'Normal'], '#00ff00',
                    ['==', ['get', 'Class'], 'Yellowish'], '#ffff00',
                    '#808080' // default grey
                  ],
                  'circle-stroke-width': 1,
                  'circle-stroke-color': '#000000'
                }}
                />
                <Layer id="palm-location-highlight-layer" type="circle" filter={['==', ['get', 'Index'], selectedPalmId != null ? selectedPalmId : -1]} paint={{
                  'circle-radius': 8,
                  'circle-color': 'transparent',
                  'circle-stroke-width': 3,
                  'circle-stroke-color': '#ff00ff'
                }} />
              </Source>
            )}
          </Map>
        </div>

        {/* Right Sidebar: Smart Thinning */}
        <div className="dashboard__right__sidebar">
          {/* Smart Thinning Container */}
          <div className="analytics__container" style={{ border: '2px solid #002D62', borderRadius: '8px', padding: '16px', display: 'flex', flexDirection: 'column', flex: 0 }}>
            <Typography variant="h6" sx={{ color: '#002D62', mb: 2, fontWeight: 'bold', textTransform: 'uppercase' }}>
              Palm Smart Thinning
            </Typography>
            {selectedJob && dashboardUrls ? (
              toolPalmDistance ? (
                <>
                  {/* Threshold inline */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '8px' }}>
                    <img src={threshold_icon} alt="Threshold" style={{ width: 38, height: 38, objectFit: 'contain' }} />
                    <Typography className="metric__value" style={{ fontSize: '24px', fontWeight: 'bold', lineHeight: 1, margin: 0, padding: 0 }}>
                      {closePlantingThreshold}m
                    </Typography>
                    <Typography className="metric__title" style={{ fontSize: '11px', margin: 0, maxWidth: '100px', lineHeight: 1.2, textAlign: 'left' }}>
                      Close Planting Threshold
                    </Typography>
                  </div>

                  {/* Summary Bottom Bar stats (Selectable filters) */}
                  <div className="metric__row" style={{ marginTop: '0px', marginBottom: '0px' }}>
                    <div
                      style={{ flex: 1, padding: '8px 4px', cursor: 'pointer', backgroundColor: thinningFilter === '1' ? '#e8f0fe' : 'transparent', borderRadius: '4px' }}
                      onClick={() => setThinningFilter(thinningFilter === '1' ? null : '1')}
                    >
                      <Typography className="metric__value" style={{ fontSize: '24px', fontWeight: 'bold', lineHeight: 1, margin: 0, padding: 0, whiteSpace: 'nowrap', textAlign: 'center', marginBottom: '4px', color: '#1e293b' }}>{palms1Close.toLocaleString()}</Typography>
                      <Typography className="metric__title" style={{ fontSize: '11px', textAlign: 'center', width: '100%' }}>1 Palm Planted Closely</Typography>
                    </div>
                    <div
                      style={{ flex: 1, padding: '8px 4px', cursor: 'pointer', backgroundColor: thinningFilter === '2' ? '#e8f0fe' : 'transparent', borderRadius: '4px' }}
                      onClick={() => setThinningFilter(thinningFilter === '2' ? null : '2')}
                    >
                      <Typography className="metric__value" style={{ fontSize: '24px', fontWeight: 'bold', lineHeight: 1, margin: 0, padding: 0, whiteSpace: 'nowrap', textAlign: 'center', marginBottom: '4px', color: '#1e293b' }}>{palms2Close.toLocaleString()}</Typography>
                      <Typography className="metric__title" style={{ fontSize: '11px', textAlign: 'center', width: '100%' }}>2 Palms Planted Closely</Typography>
                    </div>
                    <div
                      style={{ flex: 1, padding: '8px 4px', cursor: 'pointer', backgroundColor: thinningFilter === '>2' ? '#e8f0fe' : 'transparent', borderRadius: '4px' }}
                      onClick={() => setThinningFilter(thinningFilter === '>2' ? null : '>2')}
                    >
                      <Typography className="metric__value" style={{ fontSize: '24px', fontWeight: 'bold', lineHeight: 1, margin: 0, padding: 0, whiteSpace: 'nowrap', textAlign: 'center', marginBottom: '4px', color: '#dc2626' }}>{palmsMoreClose.toLocaleString()}</Typography>
                      <Typography className="metric__title" style={{ fontSize: '11px', textAlign: 'center', width: '100%' }}>{'>'}2 Palms Planted Closely</Typography>
                    </div>
                  </div>
                </>
              ) : (
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" flex={1}>
                  <Typography variant="body2" color="textSecondary" align="center">Analysis is not selected</Typography>
                </Box>
              )
            ) : (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" flex={1}>
                <Typography variant="body2" color="textSecondary" align="center">Select a completed job to view analytics.</Typography>
              </Box>
            )}
          </div>

          {/* Individual Palm Statistics Container */}
          <div className="analytics__container" style={{ border: '2px solid #002D62', borderRadius: '8px', padding: '16px', display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
            <Typography variant="h6" sx={{ color: '#002D62', mb: 1, fontWeight: 'bold', textTransform: 'uppercase' }}>
              Individual Palm Statistics
            </Typography>
            {selectedJob && dashboardUrls ? (
              <div className="closely__planted__list" style={{ flex: 1, overflowY: 'auto' }}>
                {filteredCloselyPlantedList.map((palm, idx) => (
                  <div
                    key={idx}
                    className="closely__planted__card"
                    onClick={() => handleZoomToPalm(palm)}
                    style={{ backgroundColor: selectedPalmId === palm.id ? '#e8f0fe' : 'transparent', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: '8px 4px' }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center' }}>
                      <Typography sx={{ fontSize: '19px', color: '#000', fontWeight: 'bold', lineHeight: 1.2 }}>Palm ID: {palm.id}</Typography>
                      <Typography variant="caption" sx={{ color: '#000', lineHeight: 1.2 }}>
                        {palm.x.toFixed(6)}, {palm.y.toFixed(6)}
                      </Typography>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', gap: '4px' }}>
                      {palm.PAD && (
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                          <Typography sx={{ color: '#555', fontSize: '11px' }}>Actual Palm Age:</Typography>
                          <Typography sx={{ fontSize: '13px', color: '#002D62', fontWeight: 'bold' }}>{palm.PAD}</Typography>
                        </div>
                      )}
                      {palm.Class && (
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                          <Typography sx={{ color: '#555', fontSize: '11px' }}>Palm Classification:</Typography>
                          <Typography sx={{ fontSize: '13px', color: palm.Class === 'Yellowish' ? '#b8860b' : '#1b5e20', fontWeight: 'bold' }}>{palm.Class}</Typography>
                        </div>
                      )}
                      {palm.NearPalm != null && (
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                          <Typography sx={{ color: '#555', fontSize: '11px' }}>Palms Planted Closely:</Typography>
                          <Typography sx={{ fontSize: '13px', color: palm.NearPalm > 2 ? '#dc2626' : '#002D62', fontWeight: 'bold' }}>{palm.NearPalm}</Typography>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {filteredCloselyPlantedList.length === 0 && (
                  <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 4 }}>
                    No individual palms detected.
                  </Typography>
                )}
              </div>
            ) : (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" flex={1}>
                <Typography variant="body2" color="textSecondary" align="center">Select a completed job to view analytics.</Typography>
              </Box>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default DashboardTab;